import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

const LINE_COLORS = ["#103766", "#3F8A86", "#D35E4E", "#E8A34A", "#8B5CF6", "#8AA4BF"];

const Trend = ({ diff }) => {
  if (diff > 0) return <TrendingUp className="w-4 h-4 text-[#2F7D5B]" />;
  if (diff < 0) return <TrendingDown className="w-4 h-4 text-[#C0392B]" />;
  return <Minus className="w-4 h-4 text-[#8AA4BF]" />;
};

export const NaeringUtvikling = ({ history, labels }) => {
  if (!history || history.length < 2) return null;
  const first = history[0];
  const last = history[history.length - 1];
  const rows = Object.keys(labels || {})
    .filter((k) => k !== "00")
    .map((k) => {
      const a = first.arbeidssted?.[k] || 0;
      const b = last.arbeidssted?.[k] || 0;
      return { code: k, name: labels[k], first: a, last: b, diff: b - a, pct: a ? ((b - a) / a) * 100 : null };
    })
    .filter((r) => r.first || r.last)
    .sort((a, b) => b.diff - a.diff);
  const total = rows.reduce((s, r) => s + r.diff, 0);
  const totalFirst = rows.reduce((s, r) => s + r.first, 0);
  const top6 = [...rows].sort((a, b) => b.last - a.last).slice(0, 6);
  const chartData = history.map((h) => ({ year: h.year, ...Object.fromEntries(top6.map((r) => [r.name, h.arbeidssted?.[r.code] || 0])) }));
  const estimerteAar = history.filter((h) => (h.estimert || []).length > 0);
  return (
    <div data-testid="naering-utvikling">
      <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-1">Næringsutvikling {first.year}–{last.year}: kva næringar veks og krympar?</p>
      <p className="text-sm text-[#4B647F] mb-4">
        Arbeidsplassar i kommunen (etter arbeidsstad). Totalt{" "}
        <span className={`font-medium ${total >= 0 ? "text-[#2F7D5B]" : "text-[#C0392B]"}`} data-testid="naering-total-diff">{total > 0 ? "+" : ""}{fmt(total)}</span>
        {" "}arbeidsplassar ({totalFirst ? `${total >= 0 ? "+" : ""}${dec(((total / totalFirst) * 100), 1)} %` : "—"}) sidan {first.year}.
      </p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="naering-utvikling-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.1em] text-[#8AA4BF] border-b border-[#E6E9F0]">
                <th className="py-2 pr-2 font-semibold">Næring</th>
                <th className="py-2 px-2 font-semibold text-right">{first.year}</th>
                <th className="py-2 px-2 font-semibold text-right">{last.year}</th>
                <th className="py-2 px-2 font-semibold text-right">Endring</th>
                <th className="py-2 pl-2 font-semibold text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const color = r.diff > 0 ? "text-[#2F7D5B]" : r.diff < 0 ? "text-[#C0392B]" : "text-[#8AA4BF]";
                return (
                  <tr key={r.code} className="border-b border-[#F1F4F8] text-[#1F2A36]" data-testid={`naering-row-${r.code}`}>
                    <td className="py-1.5 pr-2 flex items-center gap-2"><Trend diff={r.diff} />{r.name}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums text-[#4B647F]">{fmt(r.first)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{fmt(r.last)}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums font-medium ${color}`}>{r.diff > 0 ? "+" : ""}{fmt(r.diff)}</td>
                    <td className={`py-1.5 pl-2 text-right tabular-nums ${color}`}>{r.pct === null ? "ny" : `${r.pct > 0 ? "+" : ""}${dec(r.pct, 1)} %`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div>
          <p className="text-xs text-[#8AA4BF] mb-2">Dei seks største næringane, arbeidsplassar per år</p>
          <div className="h-[380px]" data-testid="naering-utvikling-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                {endringsLinjer(chartData)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={fmt} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={tooltipStyle} />
                <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
                {top6.map((r, i) => <Line key={r.code} type="monotone" dataKey={r.name} stroke={LINE_COLORS[i]} strokeWidth={2} dot={{ r: 3 }} />)}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      {estimerteAar.length > 0 && (
        <p className="text-xs text-[#8AA4BF] mt-3" data-testid="naering-estimat-note">
          SSB skjuler tal for næringar med få verksemder i mindre kommunar. I staden for å vise null, reknar vi ut det som manglar frå totalen for
          året (SSB «Alle næringer») og fordeler det etter kva næringa utgjer i kommunen, fylket eller landet. Det gjeld{" "}
          {estimerteAar.map((h) => h.year).join(", ")} – høgast{" "}
          {Math.max(...estimerteAar.map((h) => h.estimert.length))} av {Object.keys(labels || {}).length - 1} næringar i eitt år.
        </p>
      )}
    </div>
  );
};

export default NaeringUtvikling;
